import { Component, effect, OnInit } from '@angular/core';

import { filter, fromEvent, Observable, of, switchMap } from 'rxjs';

import { ChangeOfferSelected, Offer, Reservation } from '../../reservation.model';

import { ReservationSummaryApiService } from '../services/reservation-summary-api.service';
import { ReservationSummaryStoreService } from '../services/reservation-summary-store.service';
import { ReservationService } from '../../reservation.service';
import { LoaderService } from '../../../common/loader/loader.service';
import { InfoboxService } from '../../../common/infobox/infobox.service';
import { CancellationStatuses, Status } from '../../../common/enums/Status';
import { map, scan, take } from 'rxjs/operators';
import {
	PlaceForFive,
	PlaceForFour,
	PlaceForSingle,
	PlaceForSix,
	PlaceForThree,
	PlaceForTwo,
	RoomType,
} from '../../../common/enums/RoomType';
import { PromiseAggregator } from '../../../common/promise-aggregator';
import { toSignal } from '@angular/core/rxjs-interop';
import { ReservationSummaryAccomodationOfferRoomsDialogOfferChangeDialogComponent } from './reservation-summary-offer/reservation-summary-accomodation-offer-rooms/reservation-summary-accomodation-offer-rooms-dialog/reservation-summary-accomodation-offer-rooms-dialog-offer-change-dialog/reservation-summary-accomodation-offer-rooms-dialog-offer-change-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { deepClone } from '@/_utils/deep-clone';
import { OfferChangeTypes } from '@/common/enums/OfferChangeType';
import { ChooseOfferToChangeDialogComponent } from './choose-offer-to-change-dialog/choose-offer-to-change-dialog.component';
import { ReservationSummaryCommonService } from '../services/reservation-summary-common.service';

@Component({
	selector: 'app-reservation-summary-accomodation',
	templateUrl: './reservation-summary-accommodation.component.html',
	styleUrls: ['./reservation-summary-accommodation.component.scss'],
})
export class ReservationSummaryAccommodationComponent implements OnInit {
	reservation: Observable<Reservation>;
	readonly paste$ = fromEvent<ClipboardEvent>(document, 'paste').pipe(
		map((e) => e.clipboardData?.getData('text') ?? ''),
		scan((acc, text) => ({ text, seq: acc.seq + 1 }), { text: '', seq: 0 }),
	);

	readonly paste = toSignal(this.paste$, { initialValue: { text: '', seq: 0 } });

	private readonly clipbardParseErrorMsg =
		'Nie udało się odczytać danych ze schowka. Upewnij się, że kopiujesz ofertę bezpośrednio ze strony oskar.com.pl, będąc zalogowanym na konto sprzedawcy.';

	constructor(
		public service: ReservationService,
		private readonly infobox: InfoboxService,
		private readonly store: ReservationSummaryStoreService,
		private readonly apiService: ReservationSummaryApiService,
		private readonly loader: LoaderService,
		private readonly promiseAggregator: PromiseAggregator,
		private readonly dialog: MatDialog,
		private readonly reservationCommonService: ReservationSummaryCommonService,
	) {
		effect(() => {
			const { text, seq } = this.paste();
			if (!text) {
				return;
			}

			const decodedText = this.fromBase64<ChangeOfferSelected>(text);

			if (seq > 0 && !!text) {
				this.openChangeOfferDialog(decodedText);
			}
		});

		effect(() => {
			const offerId = this.reservationCommonService.offerIdToChange();
			if (!!offerId) {
				this.readClipboardAndOpenDialog(offerId);
			}
		});
	}

	ngOnInit(): void {
		const { reservation } = this.store.getters();

		this.reservation = reservation();
	}

	openChangeOfferDialog(selectedOffer: ChangeOfferSelected): void {
		this.reservation.pipe(take(1)).subscribe((reservation) => {
			if (!reservation) {
				return;
			}

			const offersWithNoCancellation = reservation.offer.filter(
				(v) => !CancellationStatuses.includes(v?.status as Status),
			);

			if (offersWithNoCancellation?.length > 1) {
				this.dialog
					.open(ChooseOfferToChangeDialogComponent, {
						maxWidth: '1000px',
						data: offersWithNoCancellation,
					})
					.afterClosed()
					.pipe(filter(Boolean))
					.subscribe((id: number) => {
						const offerIndex = offersWithNoCancellation.findIndex((o) => o.id === id);
						this.openDialog(reservation, offerIndex, selectedOffer);
					});
				return;
			}

			this.openDialog(reservation, 0, selectedOffer);
		});
	}

	cancelDraggingChanges(): void {
		this.service.slotDragging = false;
		this.apiService.actions().getReservation(this.service.reservation$.getValue().id);
	}

	submitSlotChanges(): void {
		this.loader.show();
		const reservation = this.service.reservation$.getValue();
		const patches = [];
		let isAnyError = false;

		reservation.offer.forEach((offer) => {
			const participantsAmount = offer.slot.filter(
				(slot) => !CancellationStatuses.includes(slot.status as Status),
			).length;
			if (offer.roomType) {
				const roomCode = offer.roomType.code as RoomType;
				if (
					(PlaceForSingle.includes(roomCode) && participantsAmount > 1) ||
					(PlaceForTwo.includes(roomCode) && participantsAmount > 2) ||
					(PlaceForThree.includes(roomCode) && participantsAmount > 3) ||
					(PlaceForFour.includes(roomCode) && participantsAmount > 4) ||
					(PlaceForFive.includes(roomCode) && participantsAmount > 5) ||
					(PlaceForSix.includes(roomCode) && participantsAmount > 6)
				) {
					isAnyError = true;
					this.loader.hide();
					return this.infobox.error(
						'Conajmniej jedna z ofert ma więcej uczestników niż możliwych miejsc w pokoju',
					);
				}
			}

			if (
				CancellationStatuses.includes(offer.status as Status) &&
				offer.slot.some((slot) => !CancellationStatuses.includes(slot.status as Status))
			) {
				isAnyError = true;
				this.loader.hide();
				return this.infobox.error(
					'Conajmniej jedna z anulowanych lub zrzuconych ofert posiada nie anulowanego uczestnika, zweryfikuj to',
				);
			}
			offer.slot.forEach((slot) => {
				const payload = {
					reservation_offer: offer.id,
				};

				patches.push(this.apiService.actions().patchOfferSlot(slot.id, payload));
			});
		});

		if (!isAnyError) {
			this.promiseAggregator.aggregate(patches, () => {
				this.service.slotDragging = false;
				this.apiService.actions().getReservation(this.service.reservation$.getValue().id);
				this.infobox.success('Pomyślnie zaktualiowanie rozmieszczenie uczestników między pokojami');
			});
		}
	}

	protected get isAnyCancelledOffer(): boolean {
		return this.service.reservation$
			.getValue()
			.offer.some((offer) => CancellationStatuses.includes(offer.status as Status));
	}

	protected get offers(): Observable<Offer[]> {
		const filterOffers = (offer: Offer) =>
			!CancellationStatuses.includes(offer.status as Status) ||
			offer.slot.some((slot) => slot.feeDueToCancellation > 0);

		return this.reservation.pipe(
			filter((reservation) => !!reservation),
			map((reservation) => reservation.offer),
			switchMap((offers) =>
				this.service.showAllOffers ? of(offers) : of(offers.filter((offer) => filterOffers(offer))),
			),
		);
	}

	private openDialog(reservation: Reservation, offerIndex: number, selectedOffer: ChangeOfferSelected): void {
		const offersWithNoCancellation = reservation.offer.filter(
			(v) => !CancellationStatuses.includes(v?.status as Status),
		);

		const offer = deepClone(offersWithNoCancellation?.[offerIndex]);

		if (!offer) {
			return;
		}

		const hasOskarFault = reservation.alerts?.some((a) => ['COO', 'COF'].includes(a?.id)) ?? false;

		this.dialog.open(ReservationSummaryAccomodationOfferRoomsDialogOfferChangeDialogComponent, {
			width: '1000px',
			disableClose: true,
			data: {
				currentOffer: {
					...offer,
					oskarFault: hasOskarFault,
					type: OfferChangeTypes.CHANGE,
				},
				selectedOffer,
				surchargeService: null,
				reservationStatus: reservation.status,
			},
		});
	}

	private async readClipboardAndOpenDialog(offerId: number): Promise<void> {
		try {
			const text = await navigator.clipboard.readText();
			const decodedText = this.fromBase64<ChangeOfferSelected>(text);

			this.reservation.pipe(take(1)).subscribe((reservation) => {
				if (!reservation) {
					return;
				}

				const offersWithNoCancellation = reservation.offer.filter(
					(v) => !CancellationStatuses.includes(v?.status as Status),
				);

				if (offerId) {
					const offerIndex = offersWithNoCancellation.findIndex((o) => o.id === offerId);
					this.openDialog(reservation, offerIndex, decodedText);
					return;
				}
			});
		} catch (err) {
			this.infobox.warning(this.clipbardParseErrorMsg);
			console.error('Clipboard read failed:', err);
		}
		this.reservationCommonService.offerIdToChange.set(null);
	}

	private fromBase64<T>(b64: string): T {
		const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
		const json = new TextDecoder().decode(bytes);
		return JSON.parse(json);
	}
}
